import { getCurrentBillingCycle, getInvoices, updateBilling, upgradeUserPlan } from '@/System/Store/Api/ApiPayment';
import { departmentUsers, invite } from '@/System/Store/Api/ApiUser';
import {
    changePlan,
    completeOnboarding,
    completeOnboardingBanner,
    grantAccess,
} from '@/System/Store/Api/ApiDepartments';
import { getCache, ROLES, setCache, setSnackbar } from '@/helpers';
import { getRequest, postRequest, onboardingUrl, depUrl, apiDomainEmploy } from '@/System/Store/endpoints';

let btnStatus = 'pending';

const initialState = () => ({
    departments: [],
    pendingDepartments: [],
    acceptedDepartments: [],
    currentDepartment: null,
    plans: [],
    departmentUsers: [],
    invited_department_id: null,
    inviteEmail: '',
    invoice: null,
    newDepartment: {
        billing_email: '',
        billing_reference: '',
        billing_address: '',
        billing_city: '',
        billing_zipcode: '',
    },
});

const state = initialState();

const mutations = {
    UPDATE_DEPARTMENTS(state, departments) {
        state.departments = departments.departments;
        state.acceptedDepartments = departments.acceptedDepartments;
        state.pendingDepartments = departments.pendingDepartments;
        state.currentDepartment = departments.currentDepartment;
    },
    UPDATE_BILLING_FIELDS(state, obj) {
        const key = Object.keys(obj)[0];
        state.currentDepartment[key] = obj[key];
    },
    UPDATE_NEW_BILLING_FIELDS(state, obj) {
        const key = Object.keys(obj)[0];
        state.newDepartment[key] = obj[key];
    },
    CHANGE_CURRENTDEPARTMENT: (state, currentDepartment) => (state.currentDepartment = currentDepartment),
    CHANGE_ACCEPTEDDEPARTMENT: (state, arr) => (state.acceptedDepartments = arr),
    UPDATE_INVITE_EMAIL: (state, field) => (state.inviteEmail = field.inviteEmail),
    SET_DEPARTMENT_USERS: (state, users) => (state.departmentUsers = users),
    SET_INVITED_DEPARTMENT_ID: (state, departmentId) => (state.invited_department_id = departmentId),
    SET_ACCEPTED_DEPARTMENTS: (state, array) => (state.acceptedDepartments = array),
    SET_INVOICE: (state, blob) => (state.invoice = URL.createObjectURL(blob)),
    SET_CURRENT_BILLING_CYCLE: (state, cycle) => (state.currentDepartment.current_billing_cycle = cycle),
    RESET_STATE: (state) => Object.assign(state, initialState()),
};

const actions = {
    SET_DEPARTMENTS: ({ commit }, departments) => {
        let localCache = getCache();

        if (!localCache) {
            // No session in storage (expired while the request was in flight); nothing to update.
            return;
        }

        const acceptedDepartments = departments?.filter((d) => d.department_status === 'accepted');
        const pendingDepartments = departments?.filter((d) => d.department_status === 'pending');

        let department = departments.find((d) => d.id === localCache?.department_id);

        // TODO: experimental removal of this
        // if (!department) {
        //     department = acceptedDepartments[0];
        // }

        commit('CHANGE_CURRENTDEPARTMENT', department);

        localCache.currentDepartment = department || null;
        localCache.department_id = department?.id || null;
        localCache.departments = departments || [];
        localCache.acceptedDepartments = acceptedDepartments || [];
        localCache.pendingDepartments = pendingDepartments || [];

        setCache(localCache);

        console.debug('Update current department to ID', localCache.department_id);

        commit('SettingStore/SET_SETTINGS', {}, { root: true });

        commit('UPDATE_DEPARTMENTS', {
            departments: departments || [],
            acceptedDepartments: acceptedDepartments || [],
            pendingDepartments: pendingDepartments || [],
            currentDepartment: localCache?.department_id
                ? departments.filter((d) => d.id === localCache?.department_id && d.department_status === 'accepted')[0]
                : null,
        });
    },
    async UPGRADE_PLAN({ commit }) {
        await upgradeUserPlan(state.currentDepartment.plan.is_free_plan).then((response) => {
            const snackbar = setSnackbar(response);

            if (response.status === 'Success') {
                commit('CHANGE_CURRENTDEPARTMENT', response.data);
            }

            commit('GlobalStore/SET_SNACKBAR', snackbar, { root: true });
            // setTimeout(() => commit('GlobalStore/CLEAR_SNACKBAR', null, { root: true }), 3000)
        });
    },
    async SAVE_BILLING_DETAILS({ state, commit, dispatch }) {
        await commit('GlobalStore/SET_SAVE_STATUS', btnStatus, { root: true });

        await updateBilling({
            billing_address: state.currentDepartment.billing_address,
            billing_city: state.currentDepartment.billing_city,
            billing_email: state.currentDepartment.billing_email,
            billing_phone: state.currentDepartment.billing_phone,
            billing_reference: state.currentDepartment.billing_reference,
            billing_zipcode: state.currentDepartment.billing_zipcode,
        }).then((response) => {
            const snackbar = setSnackbar(response);
            const btnStatus = response.status;

            if (response.status === 'Success') {
                const cache = getCache();

                cache.currentDepartment = response.data;
                setCache(cache);

                commit('CHANGE_CURRENTDEPARTMENT', response.data);
            }

            commit('GlobalStore/SET_SAVE_STATUS', btnStatus, { root: true });
            dispatch('GlobalStore/SET_SNACKBAR', snackbar, { root: true });
        });
    },
    async SAVE_NEW_BILLING_DETAILS({ state, commit, dispatch }) {
        await commit('GlobalStore/SET_SAVE_STATUS', btnStatus, { root: true });

        await updateBilling({
            billing_address: state.newDepartment.billing_address,
            billing_city: state.newDepartment.billing_city,
            billing_email: state.newDepartment.billing_email,
            billing_phone: state.newDepartment.billing_phone,
            billing_reference: state.newDepartment.billing_reference,
            billing_zipcode: state.newDepartment.billing_zipcode,
        }).then((response) => {
            const snackbar = setSnackbar(response);
            const btnStatus = response.status;

            if (response.status === 'Success') {
                commit('CHANGE_CURRENTDEPARTMENT', response.data);
            }

            commit('GlobalStore/SET_SAVE_STATUS', btnStatus, { root: true });
            dispatch('GlobalStore/SET_SNACKBAR', snackbar, { root: true });
        });
    },
    async CHANGE_DEPARTMENT({ commit }, department_id) {
        if (department_id) {
            const department = state.departments.find((d) => d.id === department_id);

            if (department) {
                const cache = getCache();

                if (!cache) {
                    return;
                }

                cache.currentDepartment = department;
                cache.department_id = department_id;
                setCache(cache);

                await commit('CHANGE_CURRENTDEPARTMENT', department);
            }
        }
    },
    async ACCEPT_DEPARTMENT({ state, dispatch }, payload) {
        // If from invitation, set the saved department_id from it.
        if (state.invited_department_id !== null) {
            payload.department_id = state.invited_department_id;
        }

        await grantAccess(payload).then((response) => {
            const snackbar = setSnackbar(response);

            if (response.status === 'Success') {
                dispatch('SET_DEPARTMENTS', response.data.departments);
            }

            dispatch('GlobalStore/SET_SNACKBAR', snackbar, { root: true });
        });
    },
    async GRANT_USER_ACCESS({ commit, dispatch }, payload) {
        await grantAccess(payload).then((response) => {
            const snackbar = setSnackbar(response);

            if (response.status === 'Success') {
                if (response.data) {
                    if (response.data.departments) {
                        dispatch('SET_DEPARTMENTS', response.data.departments);
                    } else {
                        commit('SET_DEPARTMENT_USERS', response.data.users);
                    }
                }
            }

            dispatch('GlobalStore/SET_SNACKBAR', snackbar, { root: true });
        });
    },
    async getDepartmentUsers({ commit }) {
        await departmentUsers().then((response) => {
            if (response.status === 'Success') {
                commit('SET_DEPARTMENT_USERS', response.data);
            }
        });
    },
    async INVITE_USER_TO_DEPARTMENT({ state, commit, dispatch }) {
        commit('GlobalStore/SET_SAVE_STATUS', 'pending', { root: true });

        return await invite({ email: state.inviteEmail }).then((response) => {
            const snackbar = setSnackbar(response);
            const btnStatus = response.status;

            if (response.status === 'Success') {
                dispatch('getDepartmentUsers');
                commit('UPDATE_INVITE_EMAIL', { inviteEmail: '' });
            }

            commit('GlobalStore/SET_SAVE_STATUS', btnStatus, { root: true });
            dispatch('GlobalStore/SET_SNACKBAR', snackbar, { root: true });

            return response.status === 'Success';
        });
    },
    async CHANGE_USER_PLAN({ commit, dispatch }, payload) {
        return await changePlan(payload).then((response) => {
            const snackbar = setSnackbar(response);

            if (response.status === 'Success' && typeof response.data === 'object') {
                commit('CHANGE_CURRENTDEPARTMENT', response.data);
            }

            if (response.data === 'incomplete_billing_details') {
                snackbar.btnText = 'Betaluppgifter';
                snackbar.btnHref = () => (location.href = '/department/payment#billing');
            }

            dispatch('GlobalStore/SET_SNACKBAR', snackbar, { root: true });

            return response.status === 'Success';
        });
    },
    async CANCEL_DEPARTMENT_PLANS({ dispatch }, id) {
        return await postRequest(`${depUrl}/${id}/access`).then((response) => {
            const snackbar = setSnackbar(response);
            dispatch('GlobalStore/SET_SNACKBAR', snackbar, { root: true });

            return response.status === 'Success';
        });
    },
    async GET_USER_INVOCIES({ commit }, id) {
        await getInvoices(id).then((blob) => {
            if (typeof blob === 'object') {
                commit('SET_INVOICE', blob);
            }
        });
    },
    async GET_CURRENT_BILLING_CYCLE({ commit }) {
        await getCurrentBillingCycle(state.currentDepartment.id).then((response) => {
            if (response.status === 'Success') {
                commit('SET_CURRENT_BILLING_CYCLE', response.data);
            }
        });
    },
    async COMPLETE_ONBOARDING({ state }) {
        await completeOnboarding({ department_id: state.currentDepartment.id });
    },
    async GET_ONBOARDING_STATUS() {
        return await getRequest(onboardingUrl).then((response) => {
            if (response.status === 'Success') {
                return response.data;
            }

            return false;
        });
    },
    //employ onboarding banner for new companies/departments
    async COMPLETE_ONBOARDING_BANNER({ state }) {
        await completeOnboardingBanner({ department_id: state.currentDepartment.id });
    },
    async GET_ONBOARDING_BANNER_STATUS() {
        return await getRequest(`${apiDomainEmploy}/departments/onboarding`).then((response) => {
            if (response.status === 'Success') {
                return response.data;
            }

            return false;
        });
    },
};

const getters = {
    GET_DEPARTMENTS: (state) => state.departments,
    GET_ACCEPTED_DEPARTMENTS: (state) => state.acceptedDepartments,
    GET_PENDING_DEPARTMENTS: (state) => state.pendingDepartments,
    GET_CURRENT_DEPARTMENT: (state) => state.currentDepartment,
    GET_DEPARTMENTS_WITH_SIGN_IN_ACCESS: (state) => {
        return state.departments?.filter(
            (dep) =>
                (dep?.user_role?.id === ROLES.CERTIFIER ||
                    dep?.user_role?.id === ROLES.ORGANIZATION_ADMIN ||
                    dep?.user_role?.id === ROLES.ORGANIZATION_TEAM_ADMIN) &&
                dep?.department_status === 'accepted'
        );
    },
    GET_DEPARTMENT_USERS: (state) => state.departmentUsers,
    GET_ACTIVE_PLAN: (state) => (state.currentDepartment && state.currentDepartment.plan) || null,
    GET_ACTIVE_ADDONS: (state) =>
        (state.currentDepartment && 'addons' in state.currentDepartment && state.currentDepartment.addons) || [],
    GET_INVOICE: (state) => state.invoice,
    GET_CURRENT_BILLING_CYCLE: (state) =>
        state.currentDepartment &&
        'current_billing_cycle' in state.currentDepartment &&
        state.currentDepartment.current_billing_cycle,
    GET_CURRENT_INVOICE: (state) =>
        (state.currentDepartment &&
            'invoices' in state.currentDepartment &&
            state.currentDepartment.invoices[state.currentDepartment.invoices.length - 1]) ||
        null,
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
};
